import { computed } from 'vue'
import { useRoute } from 'vue-router'
import auth from '../services/auth/auth'

// Central view-as helper. Lets a LEAD preview the dashboard as another
// persona via ?viewAs=dev|em. Currently scoped to releases but kept
// generic so other surfaces can hook in later. Customise here (allowed
// modes, gating group, labels) rather than at every call site.

export type ViewAsMode = 'dev' | 'em'

export const VIEW_AS_GROUP = 'PLATFORM-SERVICES-LEAD'
export const VIEW_AS_MODES: ViewAsMode[] = ['dev', 'em']
export const VIEW_AS_LABEL: Record<ViewAsMode, string> = {
  dev: 'Developer',
  em: 'Engineering Manager',
}

export function useViewAs() {
  const route = useRoute()

  const canUseViewAs = computed(
    () => auth.getUser()?.groups?.includes(VIEW_AS_GROUP) ?? false,
  )

  const viewAsMode = computed<ViewAsMode | null>(() => {
    if (!canUseViewAs.value) return null
    const raw = (route.query.viewAs as string | undefined)?.toLowerCase()
    return VIEW_AS_MODES.includes(raw as ViewAsMode) ? (raw as ViewAsMode) : null
  })

  const viewAsLabel = computed(() =>
    viewAsMode.value ? VIEW_AS_LABEL[viewAsMode.value] : '',
  )

  const dashboardWithoutViewAs = computed(() => {
    const params = new URLSearchParams()
    Object.entries(route.query).forEach(([k, v]) => {
      if (k === 'viewAs' || typeof v !== 'string') return
      params.set(k, v)
    })
    const qs = params.toString()
    return route.path + (qs ? `?${qs}` : '')
  })

  return {
    canUseViewAs,
    viewAsMode,
    viewAsLabel,
    dashboardWithoutViewAs,
  }
}
